import React, { useState } from "react";
import { AssetRateCard, AssetDays, AssetTaskType } from "../types";
import { X, Trash2 } from "lucide-react";
import { BID_ITEM_STATUSES } from "../lib/bidStatus";

interface Props {
  onClose: () => void;
  onApply: (updates: Partial<AssetDays> & { assumptions?: string; internalNotes?: string; type?: string; status?: string }) => void;
  onDelete: () => void;
  selectedCount: number;
}

export function AssetBulkEditModal({ onClose, onApply, onDelete, selectedCount }: Props) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [updates, setUpdates] = useState<{
    supervisor?: string;
    concept?: string;
    dmp?: string;
    model?: string;
    rig?: string;
    retop?: string;
    cloth?: string;
    fur?: string;
    groom?: string;
    texture?: string;
    anim?: string;
    fx?: string;
    lookdev?: string;
    photoscan?: string;
    assumptions?: string;
    internalNotes?: string;
    type?: string;
    status?: string;
  }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdates((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedUpdates: any = {};
    
    (["concept", "dmp", "model", "rig", "retop", "cloth", "fur", "groom", "texture", "anim", "fx", "lookdev", "photoscan"] as AssetTaskType[]).forEach((task) => {
      if (updates[task] !== undefined && updates[task] !== "") {
        const val = parseFloat(updates[task] as string);
        if (!isNaN(val) && val >= 0) {
          parsedUpdates[task] = val;
        }
      }
    });

    if (updates.assumptions !== undefined && updates.assumptions !== "") {
      parsedUpdates.assumptions = updates.assumptions;
    }
    if (updates.internalNotes !== undefined && updates.internalNotes !== "") {
      parsedUpdates.internalNotes = updates.internalNotes;
    }
    if (updates.type !== undefined && updates.type !== "") {
      parsedUpdates.type = updates.type;
    }
    if (updates.status !== undefined && updates.status !== "") {
      parsedUpdates.status = updates.status;
    }

    onApply(parsedUpdates);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">Bulk Edit ({selectedCount} assets)</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
            <select
              name="status"
              value={updates.status || ""}
              onChange={(e) => setUpdates(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
            >
              <option value="">Leave blank to skip</option>
              {BID_ITEM_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Asset Type</label>
            <select
              name="type"
              value={updates.type || ""}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
            >
              <option value="">Leave blank to skip</option>
              <option value="character">character</option>
              <option value="creature">creature</option>
              <option value="crowd">crowd</option>
              <option value="environment">environment</option>
              <option value="fx">fx</option>
              <option value="dmp">dmp</option>
              <option value="prop">prop</option>
              <option value="vehicle">vehicle</option>
              <option value="groom">groom</option>
              <option value="contactSheet">contactSheet</option>
              <option value="utility">utility</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(["concept", "dmp", "model", "rig", "retop", "cloth", "fur", "groom", "texture", "anim", "fx", "lookdev", "photoscan"] as const).map((task) => (
              <div key={task}>
                <label className="block text-xs font-medium text-slate-600 mb-1 capitalize">
                  {task} Days
                </label>
                <input
                  type="number"
                  name={task}
                  min="0"
                  step="0.5"
                  value={updates[task] || ""}
                  onChange={handleChange}
                  placeholder="Leave blank to skip"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Assumptions</label>
            <textarea
              name="assumptions"
              value={updates.assumptions || ""}
              onChange={handleChange}
              rows={2}
              placeholder="Leave blank to skip"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Internal Notes</label>
            <textarea
              name="internalNotes"
              value={updates.internalNotes || ""}
              onChange={handleChange}
              rows={2}
              placeholder="Leave blank to skip"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none"
            />
          </div>
          <div className="pt-4 flex justify-between items-center border-t border-slate-200 mt-4">
            {showConfirmDelete ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-red-600 font-medium">Delete {selectedCount} assets?</span>
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirmDelete(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                >
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowConfirmDelete(true)}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete selected assets"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Apply to {selectedCount} Assets
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
